import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, ShoppingBasket, Tractor, Leaf } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const { admin, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("preorders");
  const [preorders, setPreorders] = useState([]);
  const [tours, setTours] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (admin === null) navigate("/admin/login");
  }, [admin, navigate]);

  useEffect(() => {
    if (!admin) return;
    Promise.all([
      axios.get(`${API}/preorders`, { headers: authHeader() }),
      axios.get(`${API}/tours`, { headers: authHeader() }),
      axios.get(`${API}/products`),
    ])
      .then(([p, t, pr]) => {
        setPreorders(p.data);
        setTours(t.data);
        setProducts(pr.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  const setAvailability = async (id, availability) => {
    try {
      const { data } = await axios.patch(
        `${API}/products/${id}/availability`,
        { availability },
        { headers: authHeader() }
      );
      setProducts((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.success(`${data.name} updated`, {
        description: availability === "sold_out" ? "Marked sold out" : `Marked ${availability}`,
      });
    } catch {
      toast.error("Could not update availability");
    }
  };

  const setOrderStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/preorders/${id}/status`,
        { status },
        { headers: authHeader() }
      );
      setPreorders((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success(`Order #${id.slice(0, 8)} marked ${status}`);
    } catch {
      toast.error("Could not update order");
    }
  };

  const setTourStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/tours/${id}/status`,
        { status },
        { headers: authHeader() }
      );
      setTours((prev) => prev.map((t) => (t.id === id ? data : t)));
      toast.success(`Tour #${id.slice(0, 8)} marked ${status}`);
    } catch {
      toast.error("Could not update tour");
    }
  };

  if (!admin) return null;

  const tabBtn = (key, label, Icon, count) => (
    <button
      data-testid={`admin-tab-${key}`}
      onClick={() => setTab(key)}
      className={`flex items-center gap-2 rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-colors focus-ring ${
        tab === key ? "bg-saffron text-obsidian" : "border border-line text-zinc-400 hover:text-saffron hover:border-saffron"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      <span className={tab === key ? "text-obsidian/70" : "text-zinc-600"}>({count})</span>
    </button>
  );

  return (
    <main data-testid="admin-dashboard" className="pt-40 pb-32 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="overline-tag mb-4">Farm Gate</p>
              <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-light text-zinc-100">
                The orders inbox
              </h1>
            </div>
            <button
              data-testid="admin-logout-button"
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="flex items-center gap-2 rounded-full border border-line px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-wine-light hover:border-wine-light transition-colors focus-ring"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>

          <div className="flex flex-wrap gap-4 mb-10">
            {tabBtn("preorders", "Pre-orders", ShoppingBasket, preorders.length)}
            {tabBtn("tours", "Tour Requests", Tractor, tours.length)}
            {tabBtn("board", "Harvest Board", Leaf, products.length)}
          </div>

          {loading ? (
            <p data-testid="admin-loading" className="text-zinc-500 text-sm">Loading…</p>
          ) : tab === "preorders" ? (
            <div data-testid="admin-preorders-list" className="flex flex-col gap-6">
              {preorders.length === 0 && <p className="text-zinc-500">No pre-orders yet. The field is quiet.</p>}
              {preorders.map((o) => (
                <article key={o.id} data-testid={`admin-preorder-${o.id.slice(0, 8)}`} className="border border-line bg-surface p-8">
                  <div className="flex flex-wrap justify-between gap-4 mb-5">
                    <div>
                      <p className="font-display text-2xl text-zinc-100">{o.name}</p>
                      <p className="text-sm text-zinc-500">
                        {o.email}{o.phone ? ` · ${o.phone}` : ""} · {fmtDate(o.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-2xl text-saffron">${o.total.toFixed(2)}</p>
                      <p className="text-xs uppercase tracking-[0.2em] text-zinc-600">#{o.id.slice(0, 8)} · {o.status}</p>
                    </div>
                  </div>
                  <ul className="text-sm text-zinc-400 flex flex-col gap-1">
                    {o.items.map((i) => (
                      <li key={i.product_id} className="flex justify-between border-b border-line/50 py-2">
                        <span>{i.name} — {i.qty} {i.unit}</span>
                        <span className="text-zinc-300">${(i.qty * i.price).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                  {o.notes && <p className="mt-4 text-sm text-zinc-500 italic">"{o.notes}"</p>}
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mr-2">Status</span>
                    {["pending", "confirmed", "fulfilled"].map((s) => (
                      <button
                        key={s}
                        data-testid={`order-${s}-${o.id.slice(0, 8)}`}
                        onClick={() => setOrderStatus(o.id, s)}
                        className={`rounded-full px-5 py-2 text-[10px] uppercase tracking-[0.15em] font-bold transition-colors focus-ring ${
                          o.status === s
                            ? s === "fulfilled"
                              ? "bg-forest text-zinc-100"
                              : s === "confirmed"
                                ? "bg-saffron text-obsidian"
                                : "bg-zinc-700 text-zinc-200"
                            : "border border-line text-zinc-500 hover:text-saffron hover:border-saffron"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          ) : tab === "tours" ? (
            <div data-testid="admin-tours-list" className="flex flex-col gap-6">
              {tours.length === 0 && <p className="text-zinc-500">No tour requests yet.</p>}
              {tours.map((t) => (
                <article key={t.id} data-testid={`admin-tour-${t.id.slice(0, 8)}`} className="border border-line bg-surface p-8 flex flex-wrap justify-between gap-4">
                  <div>
                    <p className="font-display text-2xl text-zinc-100">{t.name}</p>
                    <p className="text-sm text-zinc-500">{t.email} · requested {fmtDate(t.created_at)}</p>
                    {t.requests && <p className="mt-3 text-sm text-zinc-500 italic">"{t.requests}"</p>}
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mr-2">Status</span>
                      {["pending", "confirmed", "done"].map((s) => (
                        <button
                          key={s}
                          data-testid={`tour-${s}-${t.id.slice(0, 8)}`}
                          onClick={() => setTourStatus(t.id, s)}
                          className={`rounded-full px-5 py-2 text-[10px] uppercase tracking-[0.15em] font-bold transition-colors focus-ring ${
                            t.status === s
                              ? s === "done"
                                ? "bg-forest text-zinc-100"
                                : s === "confirmed"
                                  ? "bg-saffron text-obsidian"
                                  : "bg-zinc-700 text-zinc-200"
                              : "border border-line text-zinc-500 hover:text-saffron hover:border-saffron"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-2xl text-saffron">{t.date}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-600">party of {t.party_size} · {t.status}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div data-testid="admin-board-list" className="flex flex-col gap-4">
              <p className="text-sm text-zinc-500 mb-2">
                Flip a variety the moment a bed is picked out — the harvest board updates everywhere instantly.
              </p>
              {products.map((p) => (
                <article
                  key={p.id}
                  data-testid={`admin-board-${p.id}`}
                  className="border border-line bg-surface p-6 flex flex-wrap items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-5 min-w-0">
                    <img src={p.image} alt={p.name} className="w-14 h-14 object-cover rounded-md border border-line shrink-0" />
                    <div className="min-w-0">
                      <p className="font-display text-xl text-zinc-100 leading-tight">{p.name}</p>
                      <p className="text-xs text-zinc-500 mt-1">${p.price.toFixed(2)} / {p.unit}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { key: "available", label: "Available" },
                      { key: "limited", label: "Limited" },
                      { key: "sold_out", label: "Sold Out" },
                    ].map((s) => (
                      <button
                        key={s.key}
                        data-testid={`board-${s.key}-${p.id}`}
                        onClick={() => setAvailability(p.id, s.key)}
                        className={`rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.15em] font-bold transition-colors focus-ring ${
                          (p.availability || "available") === s.key
                            ? s.key === "sold_out"
                              ? "bg-zinc-700 text-zinc-200"
                              : s.key === "limited"
                                ? "bg-wine text-zinc-100"
                                : "bg-saffron text-obsidian"
                            : "border border-line text-zinc-500 hover:text-saffron hover:border-saffron"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
}
